import type { ProjectileBehavior, WeaponId } from '../types.js';

export type RestBehavior = 'detonate' | 'stick';

export interface WeaponDef {
  id: WeaponId;
  name: string;
  /** One-line flavor for the toolbar. */
  desc: string;
  maxDmg: number;
  radius: number;
  /** Explosion carve radius multiplier. */
  carveScale: number;
  /** Team ammo per match. */
  ammo: number;
  // Motion ---------------------------------------------------------------
  restitution: number;
  friction: number;
  gravityScale: number;
  windScale: number;
  /** Launch speed at full power (px/s). */
  speed: number;
  knockScale: number;
  // Detonation -----------------------------------------------------------
  /** Seconds until forced detonation; -1 = impact/rest only. */
  fuseS: number;
  /** Detonate on this many bounces; -1 = unlimited. */
  maxBounces: number;
  restBehavior: RestBehavior;
  // Special behaviors ------------------------------------------------------
  /** Which special behavior the projectile runs (see ProjectileBehavior). */
  behavior: ProjectileBehavior;
  /** Cluster: number of seedlets. Scatter: number of pellets. */
  count: number;
  /** Half-angle pellet spread, degrees. */
  spreadDeg: number;
  /**
   * Second-stage weapon: what a `spawnEggs` payload drops, or what a
   * `releaseBees` hive is made of. Unused by single-stage weapons.
   */
  payload?: WeaponId;
  /** Seconds between behavior stages (egg drops, bee spawns). */
  stageS: number;
  flags: {
    stunTurns: number;
    /** Acid pool hazard on detonation. */
    acid: boolean;
    /** Leaves a burning trail while in flight + fire on detonation. */
    fireTrail: boolean;
  };
}

function def(d: Partial<WeaponDef> & { id: WeaponId; name: string; desc: string }): WeaponDef {
  return {
    maxDmg: 30,
    radius: 50,
    carveScale: 1,
    ammo: 3,
    restitution: 0.35,
    friction: 0.9,
    gravityScale: 1,
    windScale: 1,
    speed: 900,
    knockScale: 1,
    fuseS: -1,
    maxBounces: -1,
    restBehavior: 'detonate',
    behavior: 'none',
    count: 1,
    spreadDeg: 0,
    stageS: 0.5,
    flags: { stunTurns: 0, acid: false, fireTrail: false },
    ...d,
  };
}

/**
 * One weapon per idea. Every entry answers "why would I pick this over the
 * others?" — a different verb, not a different damage number.
 */
export const WEAPONS: Record<WeaponId, WeaponDef> = {
  // --- area / anti-group ---------------------------------------------------
  pomegranate: def({
    id: 'pomegranate',
    name: 'Pomegranate',
    desc: 'Super weapon — bursts on impact, then rains 14 bouncy seed-bombs that crackle open on staggered fuses.',
    maxDmg: 18,
    radius: 46,
    ammo: 2,
    speed: 950,
    // Bouncy: seeds skitter across the ground through their 2–3 s fuses.
    restitution: 0.7,
    behavior: 'cluster',
    count: 14,
  }),
  grapes: def({
    id: 'grapes',
    name: 'Grapes',
    desc: 'Tap mid-air to burst it into a rain of 30 sour droplets; let it land whole and the whole cluster goes up in one fat bang.',
    // The tap path divides (no crater here); the no-tap path lands whole and
    // detonates — a deliberately big unit blast, so "just let it drop" is a
    // real tactic. These stats also feed the AI's expected-damage estimate.
    maxDmg: 52,
    radius: 95,
    ammo: 3,
    speed: 1020,
    behavior: 'split',
    count: 30, // droplets per cluster (only on the player's tap)
    maxBounces: 0,
    restitution: 0.1,
    knockScale: 1,
  }),
  watermelon: def({
    id: 'watermelon',
    name: 'Watermelon',
    desc: 'The big one. Detonates the instant it touches anything — no bounce, no roll, just crater.',
    maxDmg: 70,
    radius: 130,
    carveScale: 1.3,
    ammo: 1,
    speed: 820,
    windScale: 0.55,
    restitution: 0,
    knockScale: 1.5,
    maxBounces: 0, // first contact = boom
  }),
  coconut: def({
    id: 'coconut',
    name: 'Coconut Cannon',
    desc: 'Field artillery: one hard shell, one very big hole. Detonates on impact.',
    maxDmg: 55,
    radius: 85,
    carveScale: 1.2,
    ammo: 3,
    speed: 980,
    gravityScale: 0.75,
    windScale: 0.5,
    restitution: 0.2,
    friction: 0.9,
    knockScale: 1.4,
    maxBounces: 0,
  }),

  // --- precision / single target ------------------------------------------
  bamboo: def({
    id: 'bamboo',
    name: 'Bamboo',
    desc: 'Airgun — a flat, fast thorn. Hard to hit, brutal when it lands.',
    maxDmg: 26,
    radius: 26,
    ammo: 4,
    speed: 1350,
    gravityScale: 0.35,
    windScale: 0.25,
    restitution: 0.05,
    friction: 0.6,
    knockScale: 0.5,
    maxBounces: 0,
  }),
  beans: def({
    id: 'beans',
    name: 'Beans',
    desc: 'Auto grenade launcher — ten pods, one every 0.3 s. Drag mid-burst to walk the rest onto a new spot.',
    maxDmg: 22,
    radius: 48,
    ammo: 3,
    speed: 950,
    restitution: 0.25,
    maxBounces: 1,
    behavior: 'volley',
    count: 9, // follow-up pods (plus the lead = 10 shots per ammo)
    stageS: 0.3, // one pod every 0.3 s — 10 pods ≈ 3 s of cannon fire
    fuseS: 2, // safety pop if a pod never touches anything (they splat on contact)
  }),
  // --- living weapons ------------------------------------------------------
  hen: def({
    id: 'hen',
    name: 'Hen',
    desc: 'Flaps downrange laying eggs. Heavy bird: nearly straight flight that sags onto the target.',
    maxDmg: 0, // the hen itself does not explode — the eggs do
    radius: 20,
    ammo: 2,
    speed: 880,
    // Not a lob: hens *fly* where you aim. Tiny gravity so the path is a
    // straight line with just enough droop to land where it was pointed.
    gravityScale: 0.12,
    windScale: 0.35,
    restitution: 0.2,
    behavior: 'spawnEggs',
    payload: 'egg',
    stageS: 0.45,
    maxBounces: 0,
  }),
  beehive: def({
    id: 'beehive',
    name: 'Beehive',
    desc: 'Cracks open and releases a swarm. The bees pick their own targets.',
    maxDmg: 8,
    radius: 30,
    ammo: 2,
    speed: 920,
    restitution: 0.2,
    behavior: 'releaseBees',
    count: 6,
    stageS: 0.9,
  }),
  pigeon: def({
    id: 'pigeon',
    name: 'Pigeon',
    desc: 'Cruise missile. Locked on, low on brains, extremely committed.',
    maxDmg: 40,
    radius: 45,
    ammo: 2,
    speed: 1020,
    gravityScale: 0.5,
    windScale: 0.2,
    restitution: 0.15,
    behavior: 'homing',
    maxBounces: 0,
  }),
  carrot: def({
    id: 'carrot',
    name: 'Carrot Mortar',
    desc: 'Wooden pipe mortar — lobs three tops-off carrots across the whole map.',
    maxDmg: 24,
    radius: 45,
    carveScale: 0.9,
    ammo: 3,
    speed: 1250,
    gravityScale: 0.45,
    windScale: 0.5,
    restitution: 0.1,
    knockScale: 0.8,
    behavior: 'scatter',
    count: 3,
    spreadDeg: 6,
    maxBounces: 0,
  }),

  // --- zone control --------------------------------------------------------
  lemon: def({
    id: 'lemon',
    name: 'Lemon',
    desc: 'Sour splash that leaves a stingy acid puddle. Denies the ground.',
    maxDmg: 10,
    radius: 40,
    ammo: 3,
    speed: 950,
    restitution: 0.45,
    knockScale: 0, // sour burn, no shove — acid must not flick people around
    flags: { stunTurns: 0, acid: true, fireTrail: false },
  }),
  chili: def({
    id: 'chili',
    name: 'Molotov Chili',
    desc: 'Molotov: shatters on first contact — no bounces — then the ground burns; anyone caught skips their turn.',
    maxDmg: 24,
    radius: 50,
    ammo: 3,
    speed: 950,
    // A real molotov: the moment it touches anything it breaks and burns.
    restitution: 0,
    maxBounces: 0,
    knockScale: 0.45, // molotov shove, not a punt
    flags: { stunTurns: 0, acid: false, fireTrail: true },
  }),
  // Not in WEAPON_IDS: hens drop these, nobody selects them.
  egg: def({
    id: 'egg',
    name: 'Egg',
    desc: 'What a flying hen leaves behind.',
    maxDmg: 14,
    radius: 34,
    ammo: 0,
    restitution: 0.3,
    maxBounces: 1,
  }),
  // Not in WEAPON_IDS: a tapped grape cluster rains these, nobody selects
  // them. Pops on first touch — the stipple is the weapon, and each drop
  // hits with a wide blast (2.5× the old radius) so the rain actually zones.
  grapelet: def({
    id: 'grapelet',
    name: 'Grapelet',
    desc: 'One droplet of a shattered cluster — a wide sour pop the instant it touches anything.',
    maxDmg: 26,
    radius: 64,
    carveScale: 0.45,
    ammo: 0,
    restitution: 0,
    maxBounces: 0,
    knockScale: 0.35,
    windScale: 0.7,
  }),
};

/** Toolbar / keyboard order — grouped by what the weapon is for. */
export const WEAPON_IDS: WeaponId[] = [
  'pomegranate',
  'grapes',
  'watermelon',
  'coconut',
  'bamboo',
  'beans',
  'hen',
  'beehive',
  'pigeon',
  'carrot',
  'lemon',
  'chili',
];
